import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getArtworkCards } from '../utils/artHistoryFlowService';
import KidBackground from '../ui/backgrounds/KidBackground';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import BackButton from '../components/BackButton';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, LogOut, BookOpen, ChevronRight, Home } from 'lucide-react';
import { goToArtistCorner, goToHomeMarketing } from '../utils/navigationPaths';
import splashImage from '../assets/splash.png';

export default function MyArtHistory() {
  const { user, isAuthenticated, logout } = useAuth();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const loadCards = useCallback(() => {
    if (!user?.uid) {
      setFlows([]);
      setLoading(false);
      return;
    }
    getArtworkCards(user.uid)
      .then((list) => setFlows(list || []))
      .catch((err) => {
        console.warn('[MyArtHistory] Failed to load artwork cards:', err);
        setFlows([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user?.uid]);

  // Load on mount and when user changes
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      setFlows([]);
      return;
    }
    loadCards();
  }, [user?.uid]);

  // Refetch when user returns to this tab (e.g. after completing Step 1 in another tab or coming back from flow)
  useEffect(() => {
    const onFocus = () => {
      if (user?.uid) loadCards();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user?.uid, loadCards]);

  const handleResumeFlow = (card) => {
    const primary = card.primaryArtwork;
    if (!primary) return;
    const flowId = card.lastFlowId;
    navigate('/learn-art-history/flow', {
      state: {
        primaryArtwork: primary,
        artwork: primary,
        flowId: flowId ?? undefined,
        artworkTypeKey: card.artworkTypeKey ?? null,
        level: card.level ?? 1,
        artworkTypeLabel: card.artworkTypeLabel ?? primary?.title ?? 'Art History',
        embeddedInFlow: true,
        fromMyArtHistory: true,
      },
    });
  };

  return (
    <div className="artm-page artm-page-pad min-h-screen relative bg-[#FFF8E1] flex justify-center px-0 py-0 md:px-[2.5cm] md:py-8 lg:py-10">
      {/* Amber color splashes background - matching home page */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
      </div>
      
      {/* Splash images merged into background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-10 right-5 opacity-20"
          style={{ 
            width: '400px',
            height: '200px',
            objectFit: 'cover',
            transform: 'rotate(15deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(2px)'
          }}
        />
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
          style={{ 
            width: '300px',
            height: '150px',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%) rotate(10deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(3px)'
          }}
        />
      </div>
      <KidBackground variant="blobs" intensity={1} />

      {/* Window container — full bleed on mobile */}
      <div className="relative z-10 w-full max-w-full py-0 md:py-4">
        <div className="bg-white/95 rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 p-3 md:p-6 flex flex-col">
          {/* Compact header — Art History Learning style */}
          <header className="flex-shrink-0 sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
            <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px]">
              <p className="text-sm md:text-base font-semibold text-gray-800">
                Art History Library
              </p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={(e) => goToArtistCorner(navigate, e)}
                  className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
                  title="Artist Corner"
                >
                  <LayoutDashboard className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden md:inline ml-1">Artist Corner</span>
                </button>
                <button
                  type="button"
                  onClick={(e) => goToHomeMarketing(navigate, e)}
                  className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
                  title="Home"
                >
                  <Home className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden md:inline ml-1">Home</span>
                </button>
              </div>
            </div>
          </header>

          <p className="text-xs text-amber-600/80 mt-2 mb-3 uppercase tracking-wider font-semibold">
            Your personal archive of Art History reflections
          </p>
          {flows && flows.length > 0 && (
            <button
              type="button"
              onClick={() => navigate('/learn-art-history')}
              className="mb-4 self-start inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-medium bg-amber-100 text-amber-800 border border-amber-300/60 shadow-sm hover:bg-amber-200 transition-colors"
              title="Explore art by category"
            >
              Journey to the Art World
            </button>
          )}

          {/* Activities container with its own scroll */}
          <div className="mt-2 rounded-2xl border border-amber-100/70 bg-white/50 backdrop-blur-sm shadow-md">
            <div className="max-h-[70vh] overflow-y-auto px-4 sm:px-6 pt-6 pb-8">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
                </div>
              ) : (
                <>
              {flows && flows.length > 0 && (
                <div className="space-y-4 mb-8">
                  <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide px-1">Your artwork cards</h3>
                  <p className="text-xs text-amber-700 px-1">Each artwork can have multiple flows. Use &quot;Continue to remaining flow&quot; to pick up where you left off.</p>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {flows.map((card) => {
                      const primary = card.primaryArtwork || {};
                      const imageUrl = primary.imageUrl || '';
                      const title = primary.title || 'Art History';
                      const summary = card.lastFlowSummary || {};
                      const step1 = !!summary.step1Complete;
                      const step2 = !!summary.step2Complete;
                      const step3 = !!summary.step3Complete;
                      const uploaded = !!card.lastFlowUploadedAt;
                      const progress = uploaded
                        ? 'Uploaded'
                        : [step1 && 'Step 1', step2 && 'Step 2', step3 && 'Step 3'].filter(Boolean).join(' • ') || 'Started';
                      return (
                        <div
                          key={card.id}
                          className="rounded-2xl border border-amber-200/80 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                        >
                              <div className="flex flex-col sm:flex-row sm:min-h-[120px]">
                            <div className="relative w-full sm:w-32 h-32 sm:h-auto sm:min-h-[120px] flex-shrink-0 bg-amber-50 overflow-hidden rounded-t-2xl sm:rounded-l-2xl sm:rounded-tr-none">
                              {imageUrl ? (
                                <LazyLoadImage
                                  src={imageUrl}
                                  alt=""
                                  effect="blur"
                                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                                  wrapperClassName="absolute inset-0 w-full h-full"
                                />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <BookOpen className="w-10 h-10 text-amber-300" />
                                </div>
                              )}
                            </div>
                            <div className="p-4 flex-1 flex flex-col justify-between min-w-0">
                              <div>
                                <p className="font-semibold text-amber-900 truncate" title={title}>{title}</p>
                                <p className="text-xs text-amber-700 mt-1">{progress}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleResumeFlow(card)}
                                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-1 rounded-lg py-1.5 px-2 -ml-1"
                              >
                                {step3 ? 'View' : 'Continue to remaining flow'}
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {flows && flows.length === 0 && (
                <div className="mt-16 max-w-xl mx-auto text-center bg-white/70 backdrop-blur-md border border-amber-100 rounded-2xl px-8 py-10 shadow-sm">
                  <h2 className="text-xl sm:text-2xl font-semibold text-amber-900 mb-3">
                    Learn Art History
                  </h2>
                  <p className="text-sm sm:text-base text-amber-800/90 leading-relaxed">
                    Appreciate an artwork, compare it with another, then try a fun activity. Each artwork has its own card; you can run multiple flows per artwork (e.g. compare with different artworks).
                  </p>
                  <button
                    onClick={() => navigate('/learn-art-history')}
                    className="mt-6 px-8 py-4 rounded-2xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg"
                  >
                    Start Learn Art History
                  </button>
                </div>
              )}
            </>
          )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}